import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SEOHead from '@/components/marketplace/SEOHead';
import { SponsoredAd } from '@/components/marketplace/SponsoredAd';
import { Clock, Tag as TagIcon, X } from 'lucide-react';

const BlogList = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // #6: `?tag=<value>` filters the blog index by tag. Backend already
  // supports the `tag` query param on GET /blog/posts.
  const tag = searchParams.get('tag') || '';
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sponsoredCard, setSponsoredCard] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/blog/categories').then(r => setCategories(r.data));
    const params = {};
    if (slug) params.category_slug = slug;
    if (tag) params.tag = tag;
    api.get('/blog/posts', { params })
      .then(r => setPosts(r.data.items))
      .finally(() => setLoading(false));
    // Fetch one sponsored card for the blog list grid
    api.get('/sponsored-ads/public', { params: { placement: 'blog_card', limit: 1 } })
      .then(r => setSponsoredCard((r.data || [])[0] || null))
      .catch(() => {});
  }, [slug, tag]);

  const currentCat = slug ? categories.find(c => c.slug === slug) : null;

  return (
    <div className='indus-container py-10'>
      <SEOHead
        title={currentCat ? `${currentCat.name} · IndusFact Blog` : 'Indus Fact Blog: Industrial B2B Trade & Equipment Insights'}
        description={currentCat?.description || "Read Indus Fact's blog for expert guides on industrial classifieds, buying and selling used machinery, equipment maintenance, and B2B trade trends over India."}
        keywords='industrial blog, B2B insights, machinery guide, industrial trade India'
        canonical={typeof window !== 'undefined' ? `${window.location.origin}${slug ? `/blog/category/${slug}` : '/blog'}` : undefined}
        robots={tag ? 'noindex,follow' : undefined}
      />

      <div className='max-w-3xl'>
        <div className='text-xs uppercase tracking-wider text-muted-foreground mb-1'>IndusFact Blog</div>
        <h1 className='text-3xl sm:text-4xl font-semibold tracking-tight'>
          {tag ? `Posts tagged "${tag}"` : (currentCat?.name || 'Insights for Indian industry')}
        </h1>
        <p className='text-muted-foreground mt-2'>{currentCat?.description || 'Guides, market analysis, and lessons from the IndusFact community.'}</p>
        {tag && (
          <button
            onClick={() => setSearchParams({})}
            className='mt-3 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs hover:bg-secondary'
            data-testid='blog-clear-tag'
          >
            <X className='h-3 w-3' /> Clear tag filter
          </button>
        )}
      </div>

      <div className='mt-6 flex flex-wrap gap-2'>
        <Link to='/blog' className={`rounded-full border px-3 py-1 text-sm ${!slug ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`} data-testid='blog-category-all'>All</Link>
        {categories.map(c => (
          <Link key={c.id} to={`/blog/category/${c.slug}`} data-testid={`blog-category-${c.slug}`}
            className={`rounded-full border px-3 py-1 text-sm ${slug === c.slug ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}>{c.name} <span className='text-xs opacity-60'>({c.post_count})</span></Link>
        ))}
      </div>

      <div className='mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5'>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => <Card key={i} className='h-64 animate-pulse'></Card>)
        ) : posts.length === 0 ? (
          <Card className='p-10 text-center col-span-full text-muted-foreground'>No posts yet.</Card>
        ) : posts.flatMap((p, i) => {
          const nodes = [
            (
              <Link to={`/blog/${p.slug}`} key={p.id} data-testid='blog-post-card' className='block group'>
                <Card className='overflow-hidden h-full flex flex-col hover:shadow-md transition-shadow'>
                  {p.cover_image_url && (
                    <div className='aspect-[16/9] bg-muted overflow-hidden'>
                      <img src={p.cover_image_url} alt={p.title} className='w-full h-full object-cover group-hover:scale-[1.02] transition-transform' loading='lazy' />
                    </div>
                  )}
                  <div className='p-5 flex-1 flex flex-col'>
                    {p.category_name && <div className='text-[11px] uppercase tracking-wider text-accent mb-1'>{p.category_name}</div>}
                    <h3 className='font-semibold text-lg leading-snug group-hover:underline underline-offset-4'>{p.title}</h3>
                    {p.excerpt && <p className='text-sm text-muted-foreground mt-2 line-clamp-3'>{p.excerpt}</p>}
                    <div className='mt-auto pt-4 text-xs text-muted-foreground flex items-center gap-3'>
                      <span className='flex items-center gap-1'><Clock className='h-3 w-3' /> {new Date(p.published_at || p.created_at).toLocaleDateString()}</span>
                      {p.tags?.length > 0 && (
                        <span className='flex items-center gap-1'><TagIcon className='h-3 w-3' /> {p.tags.slice(0, 2).join(', ')}</span>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            ),
          ];
          // Interleave a sponsored card at position #4 (after 3 organic cards)
          if (i === 2 && sponsoredCard) {
            nodes.push(
              <div key='blog-sponsored-inline' data-testid='blog-list-sponsored-card' className='h-full'>
                <SponsoredAd ad={sponsoredCard} variant='card' />
              </div>
            );
          }
          return nodes;
        })}
      </div>
    </div>
  );
};

export default BlogList;
